import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth } from './context/AuthContext';
import Login from './pages/Login/Login';
import Setup from './pages/Setup/Setup';
import Dashboard from './pages/Dashboard/Dashboard';
import Clientes from './pages/Clientes/Clientes';
import Veiculos from './pages/Veiculos/Veiculos';
import Reservas from './pages/Reservas/Reservas';
import Manutencoes from './pages/Manutencoes/Manutencoes';
import Relatorios from './pages/Relatorios/Relatorios';
import Documentos from './pages/Documentos/Documentos';
import Configuracoes from './pages/Configuracoes/Configuracoes';
import Vendas from './pages/Vendas/Vendas';
import Avaliacoes from './pages/Avaliacoes/Avaliacoes';
import Layout from './components/layout/Layout';
import {
  canAccessAvaliacoes,
  canAccessConfiguracoes,
  canAccessRelatorios,
  canAccessReservas,
  canAccessVendas,
} from './utils/permissions';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#d62828',
      light: '#f25555',
      dark: '#9f1e1e',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1c1c1c',
      light: '#2b2b2b',
      dark: '#0e0e0e',
    },
    success: {
      main: '#3aa76d',
    },
    warning: {
      main: '#c97a2b',
    },
    background: {
      default: '#0b0b0b',
      paper: '#121212',
    },
    text: {
      primary: '#ffffff',
      secondary: '#c7c7c7',
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Manrope", "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
      fontWeight: 600,
    },
    h2: {
      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
      fontWeight: 600,
    },
    h3: {
      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
      fontWeight: 600,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
});

function PrivateRoute({ children }) {
  const { usuario, loading } = useAuth();

  if (loading) {
    return <div>Carregando...</div>;
  }

  return usuario ? children : <Navigate to="/login" />;
}

function RoleRoute({ children, allow }) {
  const { usuario, loading } = useAuth();

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!usuario) {
    return <Navigate to="/login" />;
  }

  if (allow && !allow(usuario)) {
    return <Navigate to="/" />;
  }

  return children;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/setup" element={<Setup />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/clientes" element={<Clientes />} />
                  <Route path="/veiculos" element={<Veiculos />} />
                  <Route
                    path="/reservas"
                    element={(
                      <RoleRoute allow={canAccessReservas}>
                        <Reservas />
                      </RoleRoute>
                    )}
                  />
                  <Route path="/manutencoes" element={<Manutencoes />} />
                  <Route
                    path="/vendas"
                    element={(
                      <RoleRoute allow={canAccessVendas}>
                        <Vendas />
                      </RoleRoute>
                    )}
                  />
                  <Route
                    path="/relatorios"
                    element={(
                      <RoleRoute allow={canAccessRelatorios}>
                        <Relatorios />
                      </RoleRoute>
                    )}
                  />
                  <Route path="/documentos" element={<Documentos />} />
                  <Route
                    path="/avaliacoes"
                    element={(
                      <RoleRoute allow={canAccessAvaliacoes}>
                        <Avaliacoes />
                      </RoleRoute>
                    )}
                  />
                  <Route
                    path="/configuracoes"
                    element={(
                      <RoleRoute allow={canAccessConfiguracoes}>
                        <Configuracoes />
                      </RoleRoute>
                    )}
                  />
                </Routes>
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
